import Stack from '@mui/material/Stack';
import { FC, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Popover from '@mui/material/Popover';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { Iconify } from '@mgm/common/components';
import { BaseImage, BaseLabel } from '@mgm/common/components/base';
import { BaseChip } from '../../../../components/base';
import { useAppSelector } from '../../../../hooks/useAppStore';
import { getCommissionPlans } from '../../../../store/commissions/selectors';
import { getCurrencyByCode } from '../../../../store/common/selectors';

type TPlanPopover = {
  planId: number;
};

const PlanPopover: FC<TPlanPopover> = ({ planId }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const commissionPlans = useAppSelector(getCommissionPlans);
  const getCurrency = useAppSelector(getCurrencyByCode);
  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);
  const { t } = useTranslation(['inputs', 'commissions']);

  const selectedCommission = useMemo(() => commissionPlans.find((plan) => plan.id === planId), [planId]);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const planProperties = useMemo(() => {
    // eslint-disable-next-line no-nested-ternary
    const planIcon = selectedCommission?.type === 'Products' ? 'fluent-mdl2:product-variant' : selectedCommission?.type === 'Fixed' ? 'mdi:lock-rate-outline' : 'lucide-lab:stairs-arrow-up-right';

    // eslint-disable-next-line no-nested-ternary
    const planColor = selectedCommission?.type === 'Products' ? '#4a599f' : selectedCommission?.type === 'Fixed' ? '#A855F7' : '#005249';

    // eslint-disable-next-line no-nested-ternary
    const labelColor = selectedCommission?.type === 'Products' ? 'secondary' : selectedCommission?.type === 'Fixed' ? 'primary' : 'success';
    return {
      planColor,
      planIcon,
      labelColor: labelColor as 'primary'
      | 'secondary'
      | 'info'
      | 'success'
      | 'warning'
      | 'error',
    };
  }, [selectedCommission]);

  return (
    <>
      <Box
        alignItems="center"
        pr={1}
        aria-owns={open ? 'mouse-over-popover' : undefined}
        aria-haspopup="true"
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
      >
        <BaseChip
          color={planProperties?.planColor}
          icon={(
            <Iconify
              icon={planProperties?.planIcon}
              width={18}
              sx={{
                color: planProperties?.planColor,
              }}
            />
          )}
          label={t(`${selectedCommission?.type?.toLowerCase()}`, { ns: 'commissions' })}
        />
      </Box>
      <Popover
        id="mouse-over-popover"
        sx={{
          pointerEvents: 'none',
        }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        <Stack sx={{ width: selectedCommission?.type === 'Products' ? 350 : 200, py: 2, px: 2 }}>
          <Box>
            <BaseLabel
              color={planProperties.labelColor}
              letterSpacing="-0.5px"
            >
              {t('name')}
            </BaseLabel>
            <Typography variant="body2">{selectedCommission?.name}</Typography>
          </Box>
          {selectedCommission?.type === 'Products' && (
            <Box pt={1}>
              <BaseLabel
                color="secondary"
                letterSpacing="-0.5px"
              >
                {t('commission', { ns: 'commissions' })}
              </BaseLabel>
              {selectedCommission?.productCommissions?.map((item, idx) => (
                <Stack alignItems="center" key={idx?.toString()} p={1} direction="row">
                  <BaseImage
                    alt={item?.title || 'Product'}
                    src={item.imageUrl}
                    sx={{ borderRadius: 1.5, width: 40, height: 40 }}
                  />
                  <Typography px={1} variant="body2">
                    {item.displayName}
                    {' '}
                    ---
                    {' '}
                    {' '}
                    {item.fixedType === 'flat'
                      ? `${getCurrency().mark}${item.fixedAmount}`
                      : `${item.fixedAmount}%`}
                  </Typography>
                </Stack>
              ))}
            </Box>
          )}
          {selectedCommission?.type === 'Fixed' ? (
            <Box pt={1}>
              <BaseLabel
                color="primary"
                letterSpacing="-0.5px"
              >
                {t('commission', { ns: 'commissions' })}
              </BaseLabel>
              <Typography variant="caption">
                {selectedCommission?.fixedCommission.fixedAmount}
                {' '}
                {selectedCommission?.fixedCommission.fixedType === 'flat' ? getCurrency().mark : '%'}
                {' '}
                {t('perSale', { ns: 'commissions' })}
              </Typography>
            </Box>
          ) : (
            selectedCommission?.type === 'Steps' && (
              <Box pt={1}>
                <BaseLabel
                  color="success"
                  letterSpacing="-0.5px"
                >
                  {t('commissionPlans', { ns: 'commissions' })}
                </BaseLabel>
                <Stack direction="column">
                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Stack direction="column" alignItems="center">
                      <Typography variant="overline">
                        {Number(selectedCommission.commissionSteps[0].percent) > 0
                          ? '%' : getCurrency().mark}
                        {selectedCommission.commissionSteps[0].amount || selectedCommission.commissionSteps[0].percent}
                      </Typography>
                    </Stack>
                    <Stack direction="column" alignItems="center">
                      <Typography alignItems="center" display="flex" variant="caption">
                        {isMobile ? '-' : '---'}
                      </Typography>
                    </Stack>
                    <Stack direction="column" alignItems="center">
                      <Typography variant="overline">
                        {/* eslint-disable-next-line no-unsafe-optional-chaining */}
                        {Number(selectedCommission.commissionSteps[Number(selectedCommission?.commissionSteps?.length - 1)].percent) > 0
                          ? '%' : getCurrency().mark}
                        {/* eslint-disable-next-line no-unsafe-optional-chaining */}
                        {selectedCommission.commissionSteps[Number(selectedCommission?.commissionSteps?.length - 1)]?.amount || selectedCommission.commissionSteps[Number(selectedCommission?.commissionSteps?.length - 1)]?.percent}
                      </Typography>
                    </Stack>
                    <Stack direction="column" alignItems="center">
                      <Typography variant="caption">
                        {t('perSale', { ns: 'commissions' })}
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>

              </Box>
            )
          )}
        </Stack>
      </Popover>
    </>
  );
};

export {
  PlanPopover,
};
